import { ref, computed } from 'vue';
import IntervalTree from '@flatten-js/interval-tree';
import { checkAttributes } from 'dive-common/use/useActions';
import { intersection } from 'lodash';
function isTrack(value) {
    return value.features !== undefined;
}
/**
 * BaseAnnotationStore performs operations on a collection of annotations, such as
 * add and remove.  Operations on individual annotations, such as setting
 * and deleting detections, should be performed directly on the annotation
 * object.  BaseAnnotationStore will observe these changes and react if necessary.
 */
export default class BaseAnnotationStore {
    constructor({ markChangesPending, cameraName }) {
        this.markChangesPending = markChangesPending;
        this.cameraName = cameraName;
        this.annotationMap = new Map();
        this.annotationIds = ref([]);
        this.intervalTree = new IntervalTree();
        this.canary = ref(0);
        this.sorted = computed(() => {
            this.depend();
            return this.annotationIds.value
                .map((trackId) => this.get(trackId))
                .sort((a, b) => a.begin - b.begin);
        });
    }
    /**
     * By accessing the canary.value, depend sets up a dependency
     * on the notifier, allowing dependants to re-compute.
     *
     * Using Vue reactivity hooks naturally debounces updates to the canary.
     */
    depend() {
        return this.canary.value;
    }
    get(annotationId) {
        const value = this.annotationMap.get(annotationId);
        if (value === undefined) {
            throw new Error(`Annotation ID ${annotationId} not found in annotationMap.`);
        }
        return value;
    }
    /**
     * Some instances require returning the undefined value for checking purposes
     * That requires setting the error value to false
     */
    getPossible(annotationId) {
        return this.annotationMap.get(annotationId);
    }
    getFromAction(trackAction, user) {
        const tracksFound = [];
        let skipRest = false;
        let foundFrame = -1;
        this.annotationMap.forEach((track) => {
            if (skipRest) {
                return;
            }
            // Find a track which matches the specifications
            const vals = [];
            if (trackAction.startTrack !== undefined) {
                vals.push(track.id > trackAction.startTrack);
            }
            if (trackAction.startFrame !== undefined) {
                if (trackAction.direction && trackAction.direction === 'previous') {
                    vals.push(track.begin < trackAction.startFrame);
                }
                else {
                    vals.push(track.end > trackAction.startFrame);
                }
            }
            if (trackAction.typeFilter !== undefined) {
                const types = track.confidencePairs.map((item) => item[0]);
                vals.push(intersection(types, trackAction.typeFilter).length > 0);
            }
            if (trackAction.confidenceFilter !== undefined) {
                const confidenceVals = track.confidencePairs.map((item) => item[1]);
                vals.push(confidenceVals[0] > trackAction.confidenceFilter);
            }
            //attribute checking
            if (trackAction.attributes) {
                if (trackAction.attributes.track) {
                    vals.push(checkAttributes(trackAction.attributes.track, track.attributes, user));
                }
                //Need a separate check for detection attributes
                if (trackAction.attributes.detection) {
                    for (let i = 0; i < track.features.length; i += 1) {
                        const feature = track.features[i];
                        if (trackAction.startFrame !== undefined) {
                            if (trackAction.direction && trackAction.direction === 'previous') {
                                if (feature.frame >= trackAction.startFrame) {
                                    // eslint-disable-next-line no-continue
                                    continue;
                                }
                            }
                            else if (feature.frame <= trackAction.startFrame) {
                                // eslint-disable-next-line no-continue
                                continue;
                            }
                        }
                        if (feature.attributes) {
                            const result = checkAttributes(trackAction.attributes.detection, feature.attributes, user);
                            if (result) {
                                vals.push(result);
                                if (trackAction.direction && trackAction.direction === 'previous' && trackAction.startFrame) {
                                    if (foundFrame < trackAction.startFrame) {
                                        foundFrame = feature.frame;
                                    }
                                }
                                else {
                                    foundFrame = feature.frame;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            if (vals.filter((item) => item).length === vals.length) {
                tracksFound.push(track.id);
            }
            // Skip full track list if we have the necessary values
            // eslint-disable-next-line max-len
            if ((trackAction.Nth === undefined && tracksFound.length) || (trackAction.Nth !== undefined && trackAction.Nth >= 0 && tracksFound.length >= trackAction.Nth)) {
                skipRest = true;
            }
        });
        let returnTrack = -1;
        if (trackAction.Nth !== undefined) {
            if (trackAction.Nth >= 0 && tracksFound.length) {
                returnTrack = tracksFound[trackAction.Nth];
            }
            if (trackAction.Nth < 0 && tracksFound.length) {
                returnTrack = tracksFound[tracksFound.length + trackAction.Nth];
            }
        }
        if (tracksFound.length) {
            [returnTrack] = tracksFound;
        }
        if ((!trackAction.attributes || !Object.values(trackAction.attributes).length)
            && foundFrame === -1) {
            const foundTrack = this.annotationMap.get(returnTrack);
            if (foundTrack) {
                foundFrame = foundTrack.begin;
            }
        }
        return { track: returnTrack, frame: foundFrame };
    }
    getNewId() {
        if (this.annotationIds.value.length) {
            return this.annotationIds.value.reduce((prev, current) => Math.max(prev, current)) + 1;
        }
        return 0;
    }
    notify({ item, event, oldValue }) {
        if (event === 'bounds') {
            const oldInterval = oldValue;
            this.intervalTree.remove(oldInterval, item.id.toString());
            this.intervalTree.insert([item.begin, item.end], item.id.toString());
        }
        this.canary.value += 1;
        if (isTrack(item)) {
            this.markChangesPending({ action: 'upsert', track: item, cameraName: this.cameraName });
        }
        else {
            this.markChangesPending({ action: 'upsert', group: item, cameraName: this.cameraName });
        }
    }
    insert(value, args) {
        value.setNotifier((params) => {
            this.notify(params);
        });
        this.annotationMap.set(value.id, value);
        this.intervalTree.insert([value.begin, value.end], value.id.toString());
        if (args && args.afterId) {
            /* Insert specifically after another annotationId */
            const insertIndex = this.annotationIds.value.indexOf(args.afterId) + 1;
            this.annotationIds.value.splice(insertIndex, 0, value.id);
        }
        else {
            this.annotationIds.value.push(value.id);
        }
        if (!args?.imported) {
            if (isTrack(value)) {
                this.markChangesPending({ action: 'upsert', track: value, cameraName: this.cameraName });
            }
            else {
                this.markChangesPending({ action: 'upsert', group: value, cameraName: this.cameraName });
            }
        }
    }
    remove(annotationId, disableNotifications = false) {
        const value = this.get(annotationId);
        const range = [value.begin, value.end];
        if (!this.intervalTree.remove(range, annotationId.toString())) {
            throw new Error(`AnnotationId ${annotationId} with range ${range} not found in tree.`);
        }
        value.setNotifier(undefined);
        this.annotationMap.delete(annotationId);
        const listIndex = this.annotationIds.value.findIndex((v) => v === annotationId);
        if (listIndex === -1) {
            throw new Error(`AnnotationId ${annotationId} not found in annotationIds.`);
        }
        this.annotationIds.value.splice(listIndex, 1);
        if (!disableNotifications) {
            if (isTrack(value)) {
                this.markChangesPending({ action: 'delete', track: value, cameraName: this.cameraName });
            }
            else {
                this.markChangesPending({ action: 'delete', group: value, cameraName: this.cameraName });
            }
        }
    }
    clearAll() {
        this.annotationMap.clear();
        this.intervalTree.items.forEach((item) => {
            this.intervalTree.remove(item.key);
        });
        this.annotationIds.value = [];
    }
}
